<template>
  <el-container class="admin-shell" :style="{ '--sidebar-width': collapsed ? '64px' : '224px' }">
    <el-aside :width="collapsed ? '64px' : '224px'" class="sidebar">
      <div class="sidebar-brand">
        <span class="brand-mark small">L</span>
        <div v-show="!collapsed">
          <small>客户自助后台</small>
        </div>
      </div>
      <el-menu :default-active="'records'" :collapse="collapsed">
        <div v-show="!collapsed" class="menu-group-label">我的业务</div>
        <el-menu-item index="records">
          <el-icon><Tickets /></el-icon>
          <span>广告记录</span>
        </el-menu-item>
      </el-menu>
      <div class="user-box">
        <el-avatar :size="34">{{ (customer.phone.value || '客').slice(-2) }}</el-avatar>
        <div v-show="!collapsed">
          <strong>{{ customer.phone.value || '客户' }}</strong>
          <small>客户账号</small>
        </div>
        <el-tooltip content="退出登录" placement="top">
          <el-button text circle @click="onLogout">
            <el-icon><SwitchButton /></el-icon>
          </el-button>
        </el-tooltip>
      </div>
    </el-aside>

    <el-container>
      <el-header class="topbar">
        <div>
          <h1>广告记录</h1>
          <p>投放记录与收益明细 · {{ today }}</p>
        </div>
        <div class="topbar-actions">
          <el-button text @click="collapsed = !collapsed">
            <el-icon><Expand v-if="collapsed" /><Fold v-else /></el-icon>
          </el-button>
          <el-button type="primary" plain :loading="customer.loading.value" @click="customer.loadAll(true)">
            <el-icon><Refresh /></el-icon>刷新数据
          </el-button>
        </div>
      </el-header>

      <el-main class="main-content" v-loading="customer.loading.value">
        <section v-if="!revenueCollapsed" class="content-panel revenue-ratio-panel">
          <div class="section-head">
            <div>
              <h2>用户收益占比</h2>
              <p>按百分比调整展示收益；保存后仅对新产生的记录生效，历史记录按改之前的比例定死。App 与后台始终同步。</p>
            </div>
            <div class="head-actions">
              <el-button @click="collapseRevenuePanel">收起</el-button>
              <el-button type="primary" :loading="customer.revenueSaving.value" @click="onSaveRevenue">
                保存设置
              </el-button>
            </div>
          </div>
          <div class="revenue-ratio-body">
            <el-form label-position="top" @submit.prevent>
              <el-form-item label="收益占比（%）">
                <el-input-number
                  v-model="revenueForm.ratio"
                  :min="0"
                  :precision="2"
                  :step="10"
                  controls-position="right"
                  class="ratio-input"
                />
                <small class="ratio-hint">例如 100 表示原收益，200 表示 2 倍。只影响保存之后的新记录。</small>
              </el-form-item>
            </el-form>
          </div>
        </section>

        <section class="stat-grid customer-stats">
          <div class="stat-card revenue-card">
            <span class="card-icon">
              <el-icon :size="16"><Coin /></el-icon>
            </span>
            <span>总收益</span>
            <strong class="revenue-value">¥ {{ totalRevenueFormatted }}</strong>
            <small>当前筛选条件下全部记录合计</small>
          </div>
          <div class="stat-card">
            <span>应用总数</span>
            <strong>{{ customer.apps.value.length }}</strong>
            <small>已接入应用</small>
          </div>
          <div class="stat-card">
            <span>设备总数</span>
            <strong>{{ customer.devices.value.length }}</strong>
            <small>全部设备</small>
          </div>
        </section>

        <AdRecordsPanel
          :filters="customer.recordFilters"
          :records="customer.records.value"
          :total="customer.recordPagination.total"
          :total-reward="customer.recordPagination.totalReward"
          :page="customer.recordPagination.page"
          :page-size="customer.recordPagination.size"
          :loading="customer.recordsLoading.value"
          :exporting="customer.recordsExporting.value"
          :groups="customer.groups.value"
          :unique-ad-platforms="customer.uniqueAdPlatforms.value"
          :unique-ad-types="customer.uniqueAdTypes.value"
          :on-clear-filters="customer.clearRecordFilters"
          hide-total-revenue
          show-export
          description="后端筛选分页；可按时间、分组、平台筛选记录"
          @search="customer.searchRecords"
          @page-change="customer.onRecordPageChange"
          @export="customer.exportRecords"
        />
      </el-main>
    </el-container>
  </el-container>

  <Teleport to="body">
    <button
      v-show="revenueCollapsed"
      type="button"
      class="revenue-float-ball"
      :style="floatBallStyle"
      :class="{ dragging: floatDragging }"
      title="点击展开收益占比"
      @pointerdown="onFloatPointerDown"
    >
      <span class="float-ball-ratio">{{ displayRatioLabel }}</span>
      <span class="float-ball-label">占比</span>
    </button>
  </Teleport>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Tickets, Refresh, SwitchButton, Fold, Expand, Coin } from '@element-plus/icons-vue'
import AdRecordsPanel from '@/components/AdRecordsPanel.vue'

const FLOAT_STORAGE_KEY = 'customerRevenueFloatPos'
const BALL_SIZE = 64

const customer = inject('customer')
const router = useRouter()

const collapsed = ref(false)
const revenueCollapsed = ref(false)
const floatDragging = ref(false)
const floatPos = reactive({
  x: typeof window !== 'undefined' ? window.innerWidth - 88 : 24,
  y: typeof window !== 'undefined' ? window.innerHeight - 140 : 120
})

const revenueForm = reactive({
  ratio: 100
})

const formatReward = (v) => ((v ?? 0) / 1000).toFixed(4)
const totalRevenueFormatted = computed(() => formatReward(customer.recordPagination.totalReward))
const today = new Intl.DateTimeFormat('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' }).format(new Date())
const displayRatioLabel = computed(() => {
  const n = Number(revenueForm.ratio ?? 100)
  if (!Number.isFinite(n)) return '100%'
  return `${Number(n.toFixed(n % 1 === 0 ? 0 : 2))}%`
})

const floatBallStyle = computed(() => ({
  left: `${floatPos.x}px`,
  top: `${floatPos.y}px`
}))

watch(
  () => customer.info.revenueDisplayRatio,
  (ratio) => {
    revenueForm.ratio = Number(ratio ?? 100)
  },
  { immediate: true }
)

function clampFloatPos(x, y) {
  const maxX = Math.max(8, window.innerWidth - BALL_SIZE - 8)
  const maxY = Math.max(8, window.innerHeight - BALL_SIZE - 8)
  return {
    x: Math.min(maxX, Math.max(8, x)),
    y: Math.min(maxY, Math.max(8, y))
  }
}

function loadFloatPos() {
  try {
    const raw = localStorage.getItem(FLOAT_STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
      Object.assign(floatPos, clampFloatPos(parsed.x, parsed.y))
    }
  } catch {
    /* ignore */
  }
}

function saveFloatPos() {
  localStorage.setItem(FLOAT_STORAGE_KEY, JSON.stringify({ x: floatPos.x, y: floatPos.y }))
}

function collapseRevenuePanel() {
  loadFloatPos()
  if (!localStorage.getItem(FLOAT_STORAGE_KEY)) {
    Object.assign(floatPos, clampFloatPos(window.innerWidth - 88, window.innerHeight - 140))
  }
  revenueCollapsed.value = true
}

function expandRevenuePanel() {
  revenueCollapsed.value = false
}

const dragState = {
  active: false,
  moved: false,
  startX: 0,
  startY: 0,
  originX: 0,
  originY: 0,
  pointerId: null
}

function onFloatPointerDown(event) {
  if (event.button != null && event.button !== 0) return
  dragState.active = true
  dragState.moved = false
  dragState.startX = event.clientX
  dragState.startY = event.clientY
  dragState.originX = floatPos.x
  dragState.originY = floatPos.y
  dragState.pointerId = event.pointerId
  floatDragging.value = true
  event.currentTarget.setPointerCapture?.(event.pointerId)
  window.addEventListener('pointermove', onFloatPointerMove)
  window.addEventListener('pointerup', onFloatPointerUp)
  window.addEventListener('pointercancel', onFloatPointerUp)
}

function onFloatPointerMove(event) {
  if (!dragState.active) return
  const dx = event.clientX - dragState.startX
  const dy = event.clientY - dragState.startY
  if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
    dragState.moved = true
  }
  Object.assign(floatPos, clampFloatPos(dragState.originX + dx, dragState.originY + dy))
}

function onFloatPointerUp() {
  if (!dragState.active) return
  const wasDrag = dragState.moved
  dragState.active = false
  floatDragging.value = false
  window.removeEventListener('pointermove', onFloatPointerMove)
  window.removeEventListener('pointerup', onFloatPointerUp)
  window.removeEventListener('pointercancel', onFloatPointerUp)
  if (wasDrag) {
    saveFloatPos()
  } else {
    expandRevenuePanel()
  }
}

function onWindowResize() {
  Object.assign(floatPos, clampFloatPos(floatPos.x, floatPos.y))
}

loadFloatPos()
window.addEventListener('resize', onWindowResize)
onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResize)
  window.removeEventListener('pointermove', onFloatPointerMove)
  window.removeEventListener('pointerup', onFloatPointerUp)
  window.removeEventListener('pointercancel', onFloatPointerUp)
})

const onSaveRevenue = async () => {
  await customer.saveRevenueDisplay({
    revenueDisplayRatio: revenueForm.ratio
  })
}

const onLogout = () => {
  customer.logout()
  router.push('/login')
}
</script>

<style scoped>
.customer-stats {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.revenue-ratio-panel {
  margin-bottom: 18px;
}

.revenue-ratio-body {
  padding: 4px 20px 16px;
}

.ratio-input {
  width: 220px;
  max-width: 100%;
}

.ratio-hint {
  display: block;
  margin-top: 8px;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.5;
}

.revenue-card {
  padding-right: 52px;
}

.card-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 8px;
}

.revenue-card::before {
  background: linear-gradient(180deg, #10b981, #34d399);
}

.revenue-card .card-icon {
  color: #10b981;
  background: color-mix(in srgb, #10b981 12%, #fff);
}

.revenue-value {
  color: #dc2626;
}

@media (max-width: 1100px) {
  .customer-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 600px) {
  .customer-stats { grid-template-columns: 1fr; }
}
</style>

<style>
.revenue-float-ball {
  position: fixed;
  z-index: 4000;
  width: 64px;
  height: 64px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  display: grid;
  place-content: center;
  gap: 1px;
  cursor: grab;
  color: #fff;
  background: linear-gradient(145deg, #1f6feb 0%, #0d9488 100%);
  box-shadow: 0 10px 28px rgba(21, 32, 51, 0.28), inset 0 0 0 1px rgba(255, 255, 255, 0.18);
  user-select: none;
  touch-action: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.revenue-float-ball:hover {
  transform: scale(1.04);
  box-shadow: 0 14px 32px rgba(21, 32, 51, 0.34), inset 0 0 0 1px rgba(255, 255, 255, 0.22);
}

.revenue-float-ball.dragging {
  cursor: grabbing;
  transform: scale(1.06);
}

.revenue-float-ball .float-ball-ratio {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.revenue-float-ball .float-ball-label {
  font-size: 10px;
  opacity: 0.9;
  line-height: 1;
}
</style>
